import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Dynamic color palette for charts - more vibrant colors
const CY_COLOR = '#2563EB';  // Bright Blue
const LY_COLOR = '#F97316';  // Orange
const VEHICLE_COLORS = ['#06B6D4', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#84CC16'];
const FINANCIER_COLOR = '#8B5CF6'; // Purple
const SALES_REP_CY_COLOR = '#10B981'; // Green
const SALES_REP_LY_COLOR = '#F59E0B'; // Amber

// Helper to get Calendar Year dates (Jan 1 to Dec 31)
const getCalendarYearDates = (year = null) => {
  const now = new Date();
  const targetYear = year || now.getFullYear();
  
  const cyStart = new Date(targetYear, 0, 1); // Jan 1
  const cyEnd = new Date(targetYear, 11, 31); // Dec 31
  
  return { cyStart, cyEnd, year: targetYear };
};

// Helper to get YTD range (Jan 1 to today) - Calendar Year
const getYTDRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), 0, 1); // Jan 1
  return { start: firstDay, end: now };
};

// Helper to get MTD range (1st of current month to today)
const getMTDRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start: firstDay, end: now };
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Format date for input field (YYYY-MM-DD)
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate Last Year date range (same period, 1 year back)
const getLastYearRange = (fromDate, toDate) => {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  
  // Subtract 1 year from both dates
  const lyFrom = new Date(from.getFullYear() - 1, from.getMonth(), from.getDate());
  const lyTo = new Date(to.getFullYear() - 1, to.getMonth(), to.getDate());
  
  return { lyFrom, lyTo };
};

// Summary Card Component — premium gradient KPI tile (Phase 5)
const SummaryCard = ({ title, value, subtitle, icon, color, lyValue }) => (
  <div className={`bg-gradient-to-br ${color} rounded-2xl p-5 text-white shadow-[0_10px_30px_-8px_rgba(0,0,0,0.25)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.35)] hover:-translate-y-1 transition-all duration-240 ease-out relative overflow-hidden`}>
    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 pointer-events-none"></div>
    <div className="absolute -bottom-12 -left-6 w-24 h-24 rounded-full bg-white/5 pointer-events-none"></div>
    <div className="flex justify-between items-start relative z-10">
      <div>
        <p className="text-[11px] uppercase tracking-[0.15em] opacity-85 font-semibold">{title}</p>
        <h3 className="text-3xl font-bold mt-1.5 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>{value}</h3>
        {subtitle && <p className="text-xs opacity-75 mt-1 font-medium">{subtitle}</p>}
        {lyValue !== undefined && (
          <p className="text-[11px] mt-2.5 opacity-90 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-md inline-block font-medium">
            LY: {lyValue}
          </p>
        )}
      </div>
      <div className="text-4xl opacity-40">{icon}</div>
    </div>
  </div>
);

// Mini Status Card — frosted glass with gradient progress
const StatusCard = ({ label, count, total, color }) => (
  <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-indigo-100/60 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-240 ease-out">
    <div className="flex justify-between items-center mb-2">
      <span className="text-sm text-slate-600 font-medium">{label}</span>
      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${color}`}>{count}</span>
    </div>
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
      <div 
        className={`h-2 rounded-full transition-all duration-500 ease-out ${color.includes('green') ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : color.includes('yellow') ? 'bg-gradient-to-r from-amber-500 to-amber-600' : color.includes('red') ? 'bg-gradient-to-r from-rose-500 to-rose-600' : 'bg-gradient-to-r from-indigo-500 to-violet-600'}`}
        style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }}
      />
    </div>
  </div>
);

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [allBookings, setAllBookings] = React.useState([]);
  const [filteredBookings, setFilteredBookings] = React.useState([]);
  const [lyFilteredBookings, setLyFilteredBookings] = React.useState([]);
  const [mtdBookings, setMtdBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  // Date range state - default to Full Calendar Year (Jan 1 to Dec 31)
  const { cyStart, cyEnd } = getCalendarYearDates();
  const [dateRange, setDateRange] = React.useState({
    from: formatDateForInput(cyStart),
    to: formatDateForInput(cyEnd)
  });
  
  const [metrics, setMetrics] = React.useState({
    totalBookings: 0,
    lyTotalBookings: 0,
    mtdBookings: 0,
    ytdBookings: 0,
    lyYtdBookings: 0,
    pendingRegistrations: 0,
    completedDeliveries: 0,
    pendingInsurance: 0,
  });
  
  const [chartData, setChartData] = React.useState({
    monthlyTrend: [],
    vehicleWise: [],
    financierWise: [],
    salesRepMonthly: [],
  });

  React.useEffect(() => {
    fetchDashboardData();
  }, []);

  // Recalculate when date range changes
  React.useEffect(() => {
    if (allBookings.length > 0) {
      calculateMetricsAndCharts(allBookings);
    }
  }, [dateRange, allBookings]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=10000`, getAuthHeader());
      const bookings = response.data;
      setAllBookings(bookings);
      calculateMetricsAndCharts(bookings);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setLoading(false);
    }
  };

  const calculateMetricsAndCharts = (bookings) => {
    const fromDate = new Date(dateRange.from);
    const toDate = new Date(dateRange.to);
    toDate.setHours(23, 59, 59);

    // Calculate Last Year range (same period, 1 year back)
    const { lyFrom, lyTo } = getLastYearRange(fromDate, toDate);
    const lyToEnd = new Date(lyTo);
    lyToEnd.setHours(23, 59, 59);

    // Filter bookings by DMS Invoice Date within selected date range (CY)
    const filtered = bookings.filter(b => {
      if (!b.dms_invoice_date) return false;
      const invoiceDate = new Date(b.dms_invoice_date);
      return invoiceDate >= fromDate && invoiceDate <= toDate;
    }).sort((a, b) => {
      const aNum = parseInt(a.booking_no) || 0;
      const bNum = parseInt(b.booking_no) || 0;
      return bNum - aNum;
    });
    setFilteredBookings(filtered);

    // Filter bookings for Last Year (same period, 1 year back)
    const lyFiltered = bookings.filter(b => {
      if (!b.dms_invoice_date) return false;
      const invoiceDate = new Date(b.dms_invoice_date);
      return invoiceDate >= lyFrom && invoiceDate <= lyToEnd;
    });
    setLyFilteredBookings(lyFiltered);

    // MTD bookings (by DMS Invoice Date)
    const mtdRange = getMTDRange();
    const mtd = bookings.filter(b => {
      if (!b.dms_invoice_date) return false;
      const invoiceDate = new Date(b.dms_invoice_date);
      return invoiceDate >= mtdRange.start && invoiceDate <= mtdRange.end;
    }).sort((a, b) => {
      const aNum = parseInt(a.booking_no) || 0;
      const bNum = parseInt(b.booking_no) || 0;
      return bNum - aNum;
    });
    setMtdBookings(mtd);

    // YTD bookings (Calendar Year - Jan 1 to today)
    const ytdRangeData = getYTDRange();
    const ytd = bookings.filter(b => {
      if (!b.dms_invoice_date) return false;
      const invoiceDate = new Date(b.dms_invoice_date);
      return invoiceDate >= ytdRangeData.start && invoiceDate <= ytdRangeData.end;
    });

    // Last Year YTD (same period last year)
    const { lyFrom: lyYtdFrom, lyTo: lyYtdTo } = getLastYearRange(ytdRangeData.start, ytdRangeData.end);
    const lyYtdToEnd = new Date(lyYtdTo);
    lyYtdToEnd.setHours(23, 59, 59);
    
    const lyYtd = bookings.filter(b => {
      if (!b.dms_invoice_date) return false;
      const invoiceDate = new Date(b.dms_invoice_date);
      return invoiceDate >= lyYtdFrom && invoiceDate <= lyYtdToEnd;
    });

    // Status metrics (from filtered bookings)
    const pendingReg = filtered.filter(b => b.rc_status === 'pending' || b.rc_status === 'applied').length;
    const completedDel = filtered.filter(b => b.delivery_date).length;
    const pendingIns = filtered.filter(b => b.policy_status === 'Not Received').length;

    setMetrics({
      totalBookings: filtered.length,
      lyTotalBookings: lyFiltered.length,
      mtdBookings: mtd.length,
      ytdBookings: ytd.length,
      lyYtdBookings: lyYtd.length,
      pendingRegistrations: pendingReg,
      completedDeliveries: completedDel,
      pendingInsurance: pendingIns,
    });

    // Prepare chart data with LY comparison
    prepareChartData(bookings, filtered, lyFiltered, fromDate, toDate);
  };

  const prepareChartData = (allBookings, cyBookings, lyBookings, cyFrom, cyTo) => {
    // Monthly trend - CY month vs LY same month (Jan vs Jan, Feb vs Feb, etc.)
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get the year from the selected range
    const cyYear = cyFrom.getFullYear();
    const lyYear = cyYear - 1;
    
    // Show all 12 months for the selected year
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      // CY month
      const cyMonthStart = new Date(cyYear, monthIndex, 1);
      const cyMonthEnd = new Date(cyYear, monthIndex + 1, 0);
      cyMonthEnd.setHours(23, 59, 59);
      
      // LY same month (previous year)
      const lyMonthStart = new Date(lyYear, monthIndex, 1);
      const lyMonthEnd = new Date(lyYear, monthIndex + 1, 0);
      lyMonthEnd.setHours(23, 59, 59);
      
      const cyCount = allBookings.filter(b => {
        if (!b.dms_invoice_date) return false;
        const d = new Date(b.dms_invoice_date);
        return d >= cyMonthStart && d <= cyMonthEnd;
      }).length;

      const lyCount = allBookings.filter(b => {
        if (!b.dms_invoice_date) return false;
        const d = new Date(b.dms_invoice_date);
        return d >= lyMonthStart && d <= lyMonthEnd;
      }).length;

      monthlyData.push({ 
        month: monthNames[monthIndex], 
        CY: cyCount, 
        LY: lyCount
      });
    }

    // Vehicle-wise breakdown with CY vs LY comparison
    const cyVehicleMap = {};
    const lyVehicleMap = {};
    
    cyBookings.forEach(b => {
      const key = `${b.brand_name || 'Unknown'} ${b.model || ''}`.trim();
      cyVehicleMap[key] = (cyVehicleMap[key] || 0) + 1;
    });
    
    lyBookings.forEach(b => {
      const key = `${b.brand_name || 'Unknown'} ${b.model || ''}`.trim();
      lyVehicleMap[key] = (lyVehicleMap[key] || 0) + 1;
    });
    
    const allVehicles = new Set([...Object.keys(cyVehicleMap), ...Object.keys(lyVehicleMap)]);
    const vehicleData = Array.from(allVehicles)
      .map(name => ({ 
        name: name.substring(0, 15), 
        CY: cyVehicleMap[name] || 0,
        LY: lyVehicleMap[name] || 0
      }))
      .sort((a, b) => b.CY - a.CY)
      .slice(0, 8);

    // Financier-wise breakdown
    const financierMap = {};
    cyBookings.forEach(b => {
      const key = b.financier_name || 'Cash/Self';
      financierMap[key] = (financierMap[key] || 0) + 1;
    });
    const financierData = Object.entries(financierMap)
      .map(([name, value]) => ({ name: name.substring(0, 12), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    // Sales Rep Month-to-Month comparison (CY month vs LY same month for each sales rep)
    // Get unique sales reps
    const allSalesReps = new Set();
    allBookings.forEach(b => {
      if (b.sales_representative) allSalesReps.add(b.sales_representative);
    });
    
    // Build monthly data for each sales rep
    const salesRepMonthlyData = [];
    
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const cyMonthStart = new Date(cyYear, monthIndex, 1);
      const cyMonthEnd = new Date(cyYear, monthIndex + 1, 0);
      cyMonthEnd.setHours(23, 59, 59);
      
      const lyMonthStart = new Date(lyYear, monthIndex, 1);
      const lyMonthEnd = new Date(lyYear, monthIndex + 1, 0);
      lyMonthEnd.setHours(23, 59, 59);
      
      const monthData = { month: monthNames[monthIndex] };
      
      // Get top 5 sales reps by CY volume
      const cySalesRepMap = {};
      const lySalesRepMap = {};
      
      allBookings.forEach(b => {
        if (!b.dms_invoice_date || !b.sales_representative) return;
        const d = new Date(b.dms_invoice_date);
        const rep = b.sales_representative;
        
        if (d >= cyMonthStart && d <= cyMonthEnd) {
          cySalesRepMap[rep] = (cySalesRepMap[rep] || 0) + 1;
        }
        if (d >= lyMonthStart && d <= lyMonthEnd) {
          lySalesRepMap[rep] = (lySalesRepMap[rep] || 0) + 1;
        }
      });
      
      // Add CY and LY totals for this month
      monthData.CY = Object.values(cySalesRepMap).reduce((a, b) => a + b, 0);
      monthData.LY = Object.values(lySalesRepMap).reduce((a, b) => a + b, 0);
      
      salesRepMonthlyData.push(monthData);
    }

    setChartData({
      monthlyTrend: monthlyData,
      vehicleWise: vehicleData,
      financierWise: financierData,
      salesRepMonthly: salesRepMonthlyData,
    });
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const setPresetRange = (preset) => {
    const now = new Date();
    let from, to;
    
    switch (preset) {
      case 'ytd':
        // Calendar Year to Date (Jan 1 to today)
        const ytdRange = getYTDRange();
        from = formatDateForInput(ytdRange.start);
        to = formatDateForInput(ytdRange.end);
        break;
      case 'mtd':
        const mtdRange = getMTDRange();
        from = formatDateForInput(mtdRange.start);
        to = formatDateForInput(mtdRange.end);
        break;
      case 'fullYear':
        // Full Calendar Year (Jan 1 to Dec 31)
        const { cyStart, cyEnd } = getCalendarYearDates();
        from = formatDateForInput(cyStart);
        to = formatDateForInput(cyEnd);
        break;
      case 'lastYear':
        // Last Calendar Year
        const lastYear = getCalendarYearDates(now.getFullYear() - 1);
        from = formatDateForInput(lastYear.cyStart);
        to = formatDateForInput(lastYear.cyEnd);
        break;
      default:
        return;
    }
    
    setDateRange({ from, to });
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </SalesLayout>
    );
  }

  // Calculate labels
  const cyYear = new Date(dateRange.from).getFullYear();
  const lyYear = cyYear - 1;
  const { lyFrom, lyTo } = getLastYearRange(new Date(dateRange.from), new Date(dateRange.to));
  const cyLabel = `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`;
  const lyLabel = `${formatDate(lyFrom.toISOString())} - ${formatDate(lyTo.toISOString())}`;
  const mtdMonthName = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Dashboard">
      <div className="space-y-6">
        {/* Quick Actions */}
        <div className="bg-white/80 backdrop-blur-xl p-4 rounded-2xl border border-indigo-100/60 shadow-sm">
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => navigate('/sales/bookings/new')} 
              data-testid="create-booking-btn"
              className="px-5 py-2.5 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
              style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }}
            >
              + Create New Booking
            </button>
            <button 
              onClick={() => navigate('/sales/bookings')} 
              data-testid="view-all-bookings-btn"
              className="px-5 py-2.5 border border-indigo-200 text-indigo-700 bg-white rounded-xl hover:bg-indigo-50 transition-all duration-180 ease-out font-medium"
            >
              View All Bookings
            </button>
            <button 
              onClick={() => navigate('/sales/multi-reports')} 
              data-testid="number-plate-reports-btn"
              className="px-5 py-2.5 border border-indigo-200 text-indigo-700 bg-white rounded-xl hover:bg-indigo-50 transition-all duration-180 ease-out font-medium"
            >
              Multi Reports
            </button>
            <button 
              onClick={() => navigate('/sales/mis-reports')} 
              data-testid="mis-reports-btn"
              className="px-5 py-2.5 border border-indigo-200 text-indigo-700 bg-white rounded-xl hover:bg-indigo-50 transition-all duration-180 ease-out font-medium"
            >
              MIS Reports
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white/80 backdrop-blur-xl p-4 rounded-2xl border border-indigo-100/60 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">Date Range (DMS Invoice Date):</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => handleDateRangeChange('from', e.target.value)}
                className="p-2 border border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400/60 focus:outline-none transition-all duration-240"
              />
              <span className="text-slate-400">to</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => handleDateRangeChange('to', e.target.value)}
                className="p-2 border border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400/60 focus:outline-none transition-all duration-240"
              />
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setPresetRange('fullYear')}
                className="px-3 py-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg hover:bg-indigo-100 transition-colors font-medium"
              >
                Full Year {cyYear}
              </button>
              <button 
                onClick={() => setPresetRange('ytd')}
                className="px-3 py-1.5 text-xs bg-violet-50 text-violet-700 border border-violet-100 rounded-lg hover:bg-violet-100 transition-colors font-medium"
              >
                YTD
              </button>
              <button 
                onClick={() => setPresetRange('mtd')}
                className="px-3 py-1.5 text-xs bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100 rounded-lg hover:bg-fuchsia-100 transition-colors font-medium"
              >
                MTD
              </button>
              <button 
                onClick={() => setPresetRange('lastYear')}
                className="px-3 py-1.5 text-xs bg-slate-50 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors font-medium"
              >
                Last Year {lyYear}
              </button>
            </div>
          </div>
          <div className="mt-2.5 text-sm text-slate-500">
            <span className="font-semibold text-indigo-600">{cyYear}:</span> {cyLabel} ({metrics.totalBookings} bookings) | 
            <span className="font-semibold text-fuchsia-600 ml-2">{lyYear}:</span> {lyLabel} ({metrics.lyTotalBookings} bookings)
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 anim-grid-stagger">
          <SummaryCard 
            title={`Year ${cyYear}`}
            value={metrics.totalBookings.toLocaleString()} 
            subtitle={cyLabel}
            icon="📊"
            color="from-indigo-600 via-indigo-700 to-violet-700"
            lyValue={`${metrics.lyTotalBookings.toLocaleString()} (${lyYear})`}
          />
          <SummaryCard 
            title="MTD (This Month)" 
            value={metrics.mtdBookings.toLocaleString()} 
            subtitle={mtdMonthName}
            icon="📅"
            color="from-violet-600 via-purple-700 to-fuchsia-700"
          />
          <SummaryCard 
            title={`YTD ${cyYear}`}
            value={metrics.ytdBookings.toLocaleString()} 
            subtitle="Jan 1 to Today"
            icon="📈"
            color="from-fuchsia-600 via-pink-600 to-rose-600"
            lyValue={`${metrics.lyYtdBookings.toLocaleString()} (${lyYear})`}
          />
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 anim-grid-stagger">
          <StatusCard 
            label="Pending Registrations" 
            count={metrics.pendingRegistrations} 
            total={metrics.totalBookings}
            color="bg-yellow-100 text-yellow-800"
          />
          <StatusCard 
            label="Completed Deliveries" 
            count={metrics.completedDeliveries} 
            total={metrics.totalBookings}
            color="bg-green-100 text-green-800"
          />
          <StatusCard 
            label="Pending Insurance" 
            count={metrics.pendingInsurance} 
            total={metrics.totalBookings}
            color="bg-red-100 text-red-800"
          />
        </div>

        {/* MTD Report Section */}
        <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl border border-indigo-100/60 shadow-sm hover:shadow-md transition-all duration-240">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-800">
              MTD Report ({mtdMonthName})
              <span className="ml-2 text-sm font-normal text-gray-500">
                (Based on DMS Invoice Date)
              </span>
            </h3>
            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
              {mtdBookings.length} invoiced
            </span>
          </div>
          
          {mtdBookings.length > 0 ? (
            <div className="overflow-x-auto rounded-2xl border border-indigo-100/70 shadow-sm">
              <table className="w-full text-sm" data-testid="mtd-bookings-table">
                <thead style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                  <tr>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">B.No</th>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Customer</th>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Mobile</th>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Vehicle</th>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">DMS Invoice Date</th>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Reg No</th>
                    <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mtdBookings.slice(0, 10).map((b, idx) => (
                    <tr key={b.id} className={`border-b border-indigo-50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 transition-all duration-180 ${idx % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30'}`}>
                      <td className="p-3 font-semibold text-indigo-700">{b.booking_no}</td>
                      <td className="p-3 text-slate-700">{b.customer_first_name} {b.customer_last_name}</td>
                      <td className="p-3 text-slate-700">{b.mobile_no_1 || '-'}</td>
                      <td className="p-3 text-slate-700">{b.brand_name} {b.model}</td>
                      <td className="p-3 text-slate-700">{formatDate(b.dms_invoice_date)}</td>
                      <td className="p-3 font-mono text-xs text-slate-600">{b.registration_number || '-'}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/sales/bookings/${b.id}/view`)}
                            className="text-emerald-600 hover:text-emerald-800 font-semibold text-xs"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/sales/bookings/edit/${b.id}`)}
                            className="text-indigo-600 hover:text-indigo-800 font-semibold text-xs"
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mtdBookings.length > 10 && (
                <div className="mt-4 text-center pb-3">
                  <button 
                    onClick={() => navigate('/sales/bookings')}
                    className="text-indigo-600 hover:text-indigo-800 font-semibold text-sm"
                  >
                    View all {mtdBookings.length} MTD bookings →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No invoiced bookings this month yet.</p>
          )}
        </div>

        {/* Charts Row 1 - Monthly Trend: CY Month vs LY Same Month */}
        <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl border border-indigo-100/60 shadow-sm hover:shadow-md transition-all duration-240">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-800">Monthly Booking Trend ({cyYear} vs {lyYear})</h3>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CY_COLOR }}></div>
                <span>{cyYear}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LY_COLOR }}></div>
                <span>{lyYear}</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData.monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value, name) => [value, name === 'CY' ? cyYear : lyYear]}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Legend 
                formatter={(value) => value === 'CY' ? cyYear : lyYear}
              />
              <Bar dataKey="CY" fill={CY_COLOR} name="CY" radius={[4, 4, 0, 0]} />
              <Bar dataKey="LY" fill={LY_COLOR} name="LY" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="CY" stroke="#1E40AF" strokeWidth={2} dot={false} name={`${cyYear} Trend`} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Charts Row 2 - Vehicle & Sales Rep Monthly */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vehicle-wise Distribution with CY vs LY */}
          <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl border border-indigo-100/60 shadow-sm hover:shadow-md transition-all duration-240">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Vehicle-wise Distribution</h3>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-cyan-100 text-cyan-700">{cyYear}</span>
                <span className="px-2 py-1 rounded bg-purple-100 text-purple-700">{lyYear}</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData.vehicleWise} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'CY' ? cyYear : lyYear]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Legend formatter={(value) => value === 'CY' ? cyYear : lyYear} />
                <Bar dataKey="CY" fill="#06B6D4" name="CY" radius={[0, 4, 4, 0]} />
                <Bar dataKey="LY" fill="#8B5CF6" name="LY" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sales Rep Month-to-Month Performance */}
          <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl border border-indigo-100/60 shadow-sm hover:shadow-md transition-all duration-240">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Sales Rep Performance (Month-to-Month)</h3>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700">{cyYear}</span>
                <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">{lyYear}</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData.salesRepMonthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'CY' ? `${cyYear}` : `${lyYear}`]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Legend formatter={(value) => value === 'CY' ? cyYear : lyYear} />
                <Bar dataKey="CY" fill={SALES_REP_CY_COLOR} name="CY" radius={[4, 4, 0, 0]} />
                <Bar dataKey="LY" fill={SALES_REP_LY_COLOR} name="LY" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="CY" stroke="#059669" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Financier-wise */}
        <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl border border-indigo-100/60 shadow-sm hover:shadow-md transition-all duration-240">
          <h3 className="text-lg font-bold mb-4 text-gray-800">Financier-wise Breakdown ({cyYear})</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData.financierWise} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
              <Bar dataKey="value" fill={FINANCIER_COLOR} radius={[0, 4, 4, 0]} name="Bookings" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </SalesLayout>
  );
}

export default Dashboard;
